import React, {
	useMemo,
	useState,
	useRef,
	useEffect,
	useCallback,
} from "react";
import PropTypes from "prop-types";
import {
	useReactTable,
	getCoreRowModel,
	getSortedRowModel,
	getExpandedRowModel,
	flexRender,
} from "@tanstack/react-table";
import {
	IconChevronDown,
	IconChevronRight,
	IconArrowNarrowDown,
	IconArrowNarrowUp,
} from "@tabler/icons-react";
import InteractiveTableCell from "../Tables/InteractiveTableCell";
import CustomPagination from "../Tables/CustomPagination";
import {
	mediumDateFormat,
	formatDocumentVersionLabel,
} from "../../utilities/utilities";
import {
	buildMajorVersionRows,
	computeCompareDisabledTexts,
	computeDeleteMajorVersionDisabled,
	computeDeleteSaveDisabled,
	buildVersionCommentMenuItem,
	buildPreviewMenuItem,
	buildCompareMenuItems,
} from "./versionUtils";
import { useVersionTableState } from "./useVersionTableState";
import "../Tables/react-table-base.css";
import "../Tables/SortableTable.css";

export default function ManualVersioningTable({
	tableUniqueID,
	userOverriddenSort,
	persistColumnWidths,
	documentManualVersioningData,
	majorVersionsData,
	documentList,
	documentID,
	documentDetails,
	documentLifecycleStatusObject,
	userRights,
	ableToModify,
	documentNotInUse,
	cognitoSub,
	deleteMajorVersion,
	deleteVersion,
	openGlobalDialog,
	updateVersionComment,
	updateMajorVersionComment,
	getSignedUrlForDocumentAndDownload,
	openDocumentInLauncher,
	addDocumentToCompare,
	firstDocIdInDocumentCompare,
	firstVersionIdInDocumentCompare,
	docCompareType,
	isValidLiteraFileType,
	isValidInlineFileType,
	isLiteraCompareVisible,
	isLiteraCompareEnabled,
	isInlineCompareEnabled,
	launcherInstalled,
	onPreview,
	promoteDocumentToNextMajorVersion,
}) {
	const labels = documentManualVersioningData?.labels ?? [];

	const { maxVersionNumber, deleteDisabledInfo, promotionDisabledInfo } =
		useVersionTableState({
			documentList,
			userRights,
			documentLifecycleStatusObject,
			ableToModify,
			documentNotInUse,
		});

	const data = useMemo(
		() => buildMajorVersionRows(documentList, labels, majorVersionsData),
		[documentList, labels, majorVersionsData]
	);

	const [expanded, setExpanded] = useState({});
	const [sorting, setSorting] = useState([{ id: "verNum", desc: true }]);
	const [localPage, setLocalPage] = useState(1);
	const PAGE_SIZE = 10;

	const initialColumnSizing = useMemo(() => {
		const saved = userOverriddenSort?.resizedColumns;
		if (!saved || !saved.length) return {};
		const sizing = {};
		saved.forEach((col) => {
			sizing[col.id] = col.value;
		});
		return sizing;
	}, []); // only runs on mount to read initial persisted sizes
	const [columnSizing, setColumnSizing] = useState(initialColumnSizing);

	const tableRef = useRef(null);
	const isInitialMount = useRef(true);

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		if (persistColumnWidths && tableUniqueID) {
			const resizedColumns = Object.entries(columnSizing).map(
				([id, value]) => ({ id, value })
			);
			persistColumnWidths(tableUniqueID, resizedColumns);
		}
	}, [columnSizing, persistColumnWidths, tableUniqueID]);

	const savedResizedColumns = userOverriddenSort?.resizedColumns;
	const prevSavedRef = useRef(savedResizedColumns);
	useEffect(() => {
		if (
			prevSavedRef.current?.length > 0 &&
			savedResizedColumns?.length === 0
		) {
			setColumnSizing({});
		}
		prevSavedRef.current = savedResizedColumns;
	}, [savedResizedColumns]);

	const handleResizeStart = useCallback(
		(header, e) => {
			e.preventDefault();
			const startX = e.clientX ?? e.touches[0].clientX;
			const startWidth = header.getSize();
			const colId = header.id;
			const minSize = 30;

			const onMove = (ev) => {
				const clientX = ev.clientX ?? ev.touches[0].clientX;
				const newWidth = Math.max(
					minSize,
					startWidth + clientX - startX
				);
				if (tableRef.current) {
					tableRef.current.style.setProperty(
						`--col-${colId}-size`,
						newWidth
					);
				}
			};

			const onEnd = (ev) => {
				document.removeEventListener("mousemove", onMove);
				document.removeEventListener("mouseup", onEnd);
				document.removeEventListener("touchmove", onMove);
				document.removeEventListener("touchend", onEnd);
				const clientX =
					ev.clientX ?? ev.changedTouches?.[0]?.clientX ?? startX;
				const newWidth = Math.max(
					minSize,
					startWidth + clientX - startX
				);
				setColumnSizing((prev) => ({ ...prev, [colId]: newWidth }));
			};

			document.addEventListener("mousemove", onMove);
			document.addEventListener("mouseup", onEnd);
			document.addEventListener("touchmove", onMove);
			document.addEventListener("touchend", onEnd);
		},
		[persistColumnWidths, tableUniqueID]
	);

	const majorVersionCount = data.length;
	const maxMajorVerNum = useMemo(
		() => Math.max(...data.map((r) => r.verNum), 0),
		[data]
	);

	const columns = useMemo(
		() => [
			{
				id: "verNum",
				header: "Ver",
				accessorKey: "verNum",
				size: 110,
				cell: (info) => {
					const original = info.row.original;

					if (original.isSubRow) {
						const save = original;
						const saveLabels = labels.filter(
							(l) => l.saveNumber === save.versionNumber
						);
						const isLatest =
							save.versionNumber === maxVersionNumber;

						const { literaDisabledText, inlineDisabledText } =
							computeCompareDisabledTexts({
								versionID: save.versionID,
								isLatest,
								documentID,
								firstDocIdInDocumentCompare,
								firstVersionIdInDocumentCompare,
								docCompareType,
								isValidLiteraFileType,
								isValidInlineFileType,
								isLiteraCompareVisible,
								isLiteraCompareEnabled,
								isInlineCompareEnabled,
								launcherInstalled,
								documentLifecycleStatusObject,
							});

						const saveLabel =
							saveLabels.length > 0
								? formatDocumentVersionLabel(saveLabels[0])
								: `v${save.versionNumber}.0`;

						const saveDisplay =
							saveLabels.length > 0 ? (
								saveLabels.map((l) => (
									<span
										key={`${l.major}-${l.minor}`}
										className="tagPill"
									>
										{formatDocumentVersionLabel(l)}
									</span>
								))
							) : (
								<span className="tagPill">
									v{save.versionNumber}.0
								</span>
							);

						const {
							disabled: saveDeleteDisabled,
							disabledHoverText: saveDeleteHoverText,
						} = computeDeleteSaveDisabled(save, {
							deleteDisabledInfo,
						});

						return (
							<InteractiveTableCell
								menuItems={[
									isLatest &&
									ableToModify &&
									!documentLifecycleStatusObject.disabled
										? {
												text: "Open",
												onSelect: () =>
													openDocumentInLauncher(
														documentID,
														cognitoSub
													),
											}
										: {
												text: "Download a Copy",
												onSelect: () =>
													getSignedUrlForDocumentAndDownload(
														documentID,
														save.versionID
													),
											},
									buildPreviewMenuItem({
										versionNumber: save.versionNumber,
										versionID: save.versionID,
										versionLabel: saveLabel,
										onPreview,
									}),
									buildVersionCommentMenuItem({
										menuLabel: `${saveLabel} Comment`,
										dialogLabel: saveLabel,
										currentComment: save.comment,
										disabled:
											!ableToModify ||
											documentLifecycleStatusObject.disabled,
										isMajorVersion: false,
										openGlobalDialog,
										onSubmit: (value) =>
											updateVersionComment(
												documentID,
												save.versionNumber,
												save.versionID,
												value
											),
									}),
									{
										text: `Promote to v${maxMajorVerNum + 1}`,
										disabled:
											promotionDisabledInfo.disabled,
										disabledHoverText:
											promotionDisabledInfo.disabledHoverText,
										onSelect: () =>
											promoteDocumentToNextMajorVersion(
												documentID,
												save.versionID,
												maxMajorVerNum + 1
											),
									},
									...buildCompareMenuItems({
										versionID: save.versionID,
										documentID,
										documentDetails,
										addDocumentToCompare,
										isValidLiteraFileType,
										isValidInlineFileType,
										documentLifecycleStatusObject,
										literaDisabledText,
										inlineDisabledText,
									}),
									{
										text: "Delete Save",
										disabled: saveDeleteDisabled,
										disabledHoverText: saveDeleteHoverText,
										onSelect: () =>
											deleteVersion({
												key: documentDetails.prefix,
												documentID,
												versionID: save.versionID,
												versionNumber:
													save.versionNumber,
												versionLabel: saveLabel,
												itemNoun: "Save",
											}),
									},
								]}
							>
								<span>└{saveDisplay}</span>
							</InteractiveTableCell>
						);
					}

					// Version (parent) row
					const defunct = original.saveCount === 0;
					const pill = (
						<span
							className="tagPill"
							style={defunct ? { opacity: 0.4 } : undefined}
						>
							{original.ver}
						</span>
					);
					if (defunct) return pill;

					const latestSubRow = original.isAutoVersioned
						? original
						: [...original.subRows].sort(
								(a, b) => b.versionNumber - a.versionNumber
							)[0];
					const latestVersionID = latestSubRow?.versionID;
					const parentIsLatest =
						latestSubRow?.versionNumber === maxVersionNumber;

					const {
						literaDisabledText: parentLiteraDisabledText,
						inlineDisabledText: parentInlineDisabledText,
					} = computeCompareDisabledTexts({
						versionID: latestVersionID,
						isLatest: parentIsLatest,
						documentID,
						firstDocIdInDocumentCompare,
						firstVersionIdInDocumentCompare,
						docCompareType,
						isValidLiteraFileType,
						isValidInlineFileType,
						isLiteraCompareVisible,
						isLiteraCompareEnabled,
						isInlineCompareEnabled,
						launcherInstalled,
						documentLifecycleStatusObject,
					});

					const {
						disabled: versionDeleteDisabled,
						disabledHoverText: versionDeleteHoverText,
					} = computeDeleteMajorVersionDisabled(original, {
						deleteDisabledInfo,
						majorVersionCount,
						maxMajorVerNum,
					});

					return (
						<InteractiveTableCell
							menuItems={[
								parentIsLatest &&
								ableToModify &&
								!documentLifecycleStatusObject.disabled
									? {
											text: "Open",
											onSelect: () =>
												openDocumentInLauncher(
													documentID,
													cognitoSub
												),
										}
									: {
											text: "Download a Copy",
											onSelect: () =>
												getSignedUrlForDocumentAndDownload(
													documentID,
													latestVersionID
												),
										},
								buildPreviewMenuItem({
									versionNumber: latestSubRow.versionNumber,
									versionID: latestVersionID,
									versionLabel: original.isAutoVersioned
										? `v${original.verNum}.0`
										: original.ver,
									onPreview,
								}),
								buildVersionCommentMenuItem({
									menuLabel: `v${original.verNum} Version Comment`,
									dialogLabel: `v${original.verNum}`,
									currentComment: original.comment,
									isMajorVersion: true,
									openGlobalDialog,
									onSubmit: (value) => {
										if (original.isAutoVersioned) {
											updateVersionComment(
												documentID,
												original.verNum,
												original.versionID,
												value
											);
										} else {
											updateMajorVersionComment(
												documentID,
												original.verNum,
												value
											);
										}
									},
								}),
								{
									text: `Promote to v${maxMajorVerNum + 1}`,
									disabled: promotionDisabledInfo.disabled,
									disabledHoverText:
										promotionDisabledInfo.disabledHoverText,
									onSelect: () =>
										promoteDocumentToNextMajorVersion(
											documentID,
											latestVersionID,
											maxMajorVerNum + 1
										),
								},
								...buildCompareMenuItems({
									versionID: latestVersionID,
									documentID,
									documentDetails,
									addDocumentToCompare,
									isValidLiteraFileType,
									isValidInlineFileType,
									documentLifecycleStatusObject,
									literaDisabledText:
										parentLiteraDisabledText,
									inlineDisabledText:
										parentInlineDisabledText,
								}),
								{
									text: "Delete this version and all saves",
									disabled: versionDeleteDisabled,
									disabledHoverText: versionDeleteHoverText,
									onSelect: () =>
										deleteMajorVersion({
											documentID,
											major: original.verNum,
										}),
								},
							]}
						>
							{pill}
						</InteractiveTableCell>
					);
				},
			},
			{
				id: "created",
				header: "Created",
				accessorFn: (row) => row.createdAt,
				size: 150,
				cell: (info) =>
					info.getValue() ? mediumDateFormat(info.getValue()) : "",
			},
			{
				id: "by",
				header: "By",
				accessorFn: (row) => row.createdBy,
				size: 100,
				cell: (info) => info.getValue() ?? "",
			},
			{
				id: "comment",
				header: "Comment",
				accessorFn: (row) => row.comment,
				size: 300,
				cell: (info) => (
					<div
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
						title={info.getValue() ?? ""}
					>
						{info.getValue() ?? ""}
					</div>
				),
			},
			{
				id: "saves",
				header: "Saves",
				accessorKey: "saveCount",
				size: 120,
				enableSorting: false,
				cell: (info) => {
					if (info.row.original.isSubRow) return null;
					const count = info.getValue() ?? 0;
					const isExpanded = info.row.getIsExpanded();
					const ChevronIcon = isExpanded
						? IconChevronDown
						: IconChevronRight;
					return (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 4,
								cursor: "pointer",
								userSelect: "none",
							}}
							onClick={info.row.getToggleExpandedHandler()}
						>
							<ChevronIcon size={14} />
							<span>
								{count} save{count !== 1 ? "s" : ""}
							</span>
						</div>
					);
				},
			},
		],
		[
			labels,
			maxVersionNumber,
			documentList,
			documentID,
			documentDetails,
			ableToModify,
			cognitoSub,
			documentLifecycleStatusObject,
			deleteDisabledInfo,
			majorVersionCount,
			maxMajorVerNum,
			promotionDisabledInfo,
			firstDocIdInDocumentCompare,
			firstVersionIdInDocumentCompare,
			docCompareType,
			isValidLiteraFileType,
			isValidInlineFileType,
			isLiteraCompareVisible,
			isLiteraCompareEnabled,
			isInlineCompareEnabled,
			launcherInstalled,
			deleteMajorVersion,
			deleteVersion,
			openGlobalDialog,
			updateVersionComment,
			updateMajorVersionComment,
			getSignedUrlForDocumentAndDownload,
			openDocumentInLauncher,
			addDocumentToCompare,
			onPreview,
		]
	);

	const table = useReactTable({
		data,
		columns,
		state: { expanded, sorting, columnSizing },
		onExpandedChange: setExpanded,
		onSortingChange: (updater) => {
			setSorting(
				typeof updater === "function" ? updater(sorting) : updater
			);
			setLocalPage(1);
		},
		onColumnSizingChange: setColumnSizing,
		getSubRows: (row) => row.subRows,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		enableSortingRemoval: false,
		enableColumnResizing: true,
		columnResizeMode: "onChange",
	});

	const topLevelRows = table.getRowModel().rows.filter((r) => r.depth === 0);
	const totalPages = Math.max(1, Math.ceil(topLevelRows.length / PAGE_SIZE));
	const safePage = Math.min(localPage, totalPages);
	const paginatedTopLevel = topLevelRows.slice(
		(safePage - 1) * PAGE_SIZE,
		safePage * PAGE_SIZE
	);
	const visibleRows = paginatedTopLevel.flatMap((row) => [
		row,
		...(row.getIsExpanded() ? row.subRows : []),
	]);

	const columnSizeVars = useMemo(() => {
		const vars = {};
		table.getFlatHeaders().forEach((header) => {
			vars[`--col-${header.id}-size`] = header.getSize();
		});
		return vars;
	}, [columnSizing, columns]);

	return (
		<div style={{ marginBottom: 24 }}>
			<div style={{ height: 470 }}>
				<div className="SortableTable -striped -highlight fullWidth">
					<div
						className="rt-table"
						ref={tableRef}
						style={columnSizeVars}
					>
						<div className="rt-thead">
							{table.getHeaderGroups().map((headerGroup) => (
								<div key={headerGroup.id} className="rt-tr">
									{headerGroup.headers.map((header) => (
										<div
											key={header.id}
											className="rt-th leftAlignedTableHeader"
											style={{
												width: `calc(var(--col-${header.id}-size) * 1px)`,
												position: "relative",
											}}
										>
											<div
												style={{
													display: "flex",
													alignItems: "center",
												}}
												onClick={header.column.getToggleSortingHandler()}
											>
												<span>
													{header.isPlaceholder
														? null
														: flexRender(
																header.column
																	.columnDef
																	.header,
																header.getContext()
															)}
												</span>
												{header.column.getIsSorted() ? (
													<span
														style={{
															marginLeft: 4,
															display: "flex",
														}}
													>
														{header.column.getIsSorted() ===
														"desc" ? (
															<IconArrowNarrowDown
																size={16}
															/>
														) : (
															<IconArrowNarrowUp
																size={16}
															/>
														)}
													</span>
												) : (
													<span
														style={{ width: 1 }}
													/>
												)}
											</div>
											{header.column.getCanResize() && (
												<div
													className="rt-resizer"
													style={{
														position: "absolute",
														right: 0,
														top: 0,
														height: "100%",
														width: 10,
														cursor: "col-resize",
														userSelect: "none",
														touchAction: "none",
													}}
													onMouseDown={(e) =>
														handleResizeStart(
															header,
															e
														)
													}
													onTouchStart={(e) =>
														handleResizeStart(
															header,
															e
														)
													}
												/>
											)}
										</div>
									))}
								</div>
							))}
						</div>
						<div className="rt-tbody">
							{visibleRows.length === 0 ? (
								<div className="rt-noData">
									<h3>No versions</h3>
								</div>
							) : (
								visibleRows.map((row) => (
									<div
										key={row.id}
										className="rt-tr-group"
										style={
											row.original.isSubRow
												? {
														background:
															"rgba(0,0,0,0.06)",
													}
												: undefined
										}
									>
										<div className="rt-tr">
											{row
												.getVisibleCells()
												.map((cell) => (
													<div
														key={cell.id}
														className="rt-td"
														style={{
															width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
														}}
													>
														<div className="rt-td-content">
															{flexRender(
																cell.column
																	.columnDef
																	.cell,
																cell.getContext()
															)}
														</div>
													</div>
												))}
										</div>
									</div>
								))
							)}
						</div>
					</div>
					<CustomPagination
						data={data}
						pages={totalPages}
						page={safePage - 1}
						pageSize={PAGE_SIZE}
						onPageChange={(p) => setLocalPage(p + 1)}
					/>
				</div>
			</div>
		</div>
	);
}

ManualVersioningTable.propTypes = {
	tableUniqueID: PropTypes.string.isRequired,
	userOverriddenSort: PropTypes.object,
	persistColumnWidths: PropTypes.func,
	documentManualVersioningData: PropTypes.shape({
		labels: PropTypes.arrayOf(
			PropTypes.shape({
				major: PropTypes.number,
				saveNumber: PropTypes.number,
			})
		),
	}),
	majorVersionsData: PropTypes.arrayOf(
		PropTypes.shape({
			majorVersionNum: PropTypes.number,
			createdAt: PropTypes.string,
			createdBy: PropTypes.string,
			comment: PropTypes.string,
		})
	),
	documentList: PropTypes.array.isRequired,
	documentID: PropTypes.number.isRequired,
	documentDetails: PropTypes.object.isRequired,
	documentLifecycleStatusObject: PropTypes.object,
	userRights: PropTypes.object,
	ableToModify: PropTypes.bool,
	documentNotInUse: PropTypes.bool,
	cognitoSub: PropTypes.string,
	deleteMajorVersion: PropTypes.func.isRequired,
	deleteVersion: PropTypes.func.isRequired,
	openGlobalDialog: PropTypes.func.isRequired,
	updateVersionComment: PropTypes.func.isRequired,
	updateMajorVersionComment: PropTypes.func.isRequired,
	getSignedUrlForDocumentAndDownload: PropTypes.func.isRequired,
	openDocumentInLauncher: PropTypes.func.isRequired,
	addDocumentToCompare: PropTypes.func.isRequired,
	firstDocIdInDocumentCompare: PropTypes.number,
	firstVersionIdInDocumentCompare: PropTypes.string,
	docCompareType: PropTypes.string,
	isValidLiteraFileType: PropTypes.bool,
	isValidInlineFileType: PropTypes.bool,
	isLiteraCompareVisible: PropTypes.bool,
	isLiteraCompareEnabled: PropTypes.bool,
	isInlineCompareEnabled: PropTypes.bool,
	launcherInstalled: PropTypes.bool,
	onPreview: PropTypes.func,
	promoteDocumentToNextMajorVersion: PropTypes.func.isRequired,
};
